import React, { useState, useEffect } from 'react';
import { FormControl } from "react-bootstrap";
import { FiClock } from "react-icons/fi";

const TimeInputWithArrows = ({ 
  value, 
  onChange, 
  onKeyDown = undefined,
  className = "", 
  isInvalid = false,
  disabled = false,
  required = false,
  id = "",
  placeholder = "",
  onBlur = undefined,
  showDecorators = true,
  showArrows = true,
  autoFocus = false,
  nextInputId = "",
  selectOnFocus = false,
  "aria-label": ariaLabel,
}) => {
  const [inputValue, setInputValue] = useState(value || "");

  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  const focusNextInput = () => {
    if (!nextInputId) return;
    requestAnimationFrame(() => {
      const nextInput = document.getElementById(nextInputId);
      if (nextInput) {
        nextInput.focus();
      }
    });
  };

  const commitValue = (newValue) => {
    setInputValue(newValue);
    if (onChange) {
      onChange(buildSyntheticEvent(newValue));
    }
  };

  // onChange dispara setState async en el padre; onBlur debe ir después para que
  // el guardado (p. ej. Mis fichajes) lea el valor ya normalizado.
  const notifyBlur = (event, deferred = false) => {
    if (!onBlur) return;
    if (deferred) {
      setTimeout(() => onBlur(event), 0);
    } else {
      onBlur(event);
    }
  };

  const parseTime = (timeStr) => {
    if (!timeStr) return { hours: 0, minutes: 0 };
    
    const parts = timeStr.split(':');
    return {
      hours: parseInt(parts[0]) || 0,
      minutes: parseInt(parts[1]) || 0
    };
  };

  const formatTime = (hours, minutes) => {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const isValidTime24h = (timeStr) => {
    if (!/^\d{2}:\d{2}$/.test(timeStr)) return false;
    const [hours, minutes] = timeStr.split(":").map((n) => parseInt(n, 10));
    return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
  };

  const buildSyntheticEvent = (newValue) => ({
    target: {
      value: newValue,
      id: id,
    },
  });

  const normalizeTo24h = (rawValue) => {
    if (!rawValue) return "";

    const compact = rawValue.trim().replace(/\s+/g, "").toUpperCase();
    const amPmMatch = compact.match(/^(\d{1,2})(?::?(\d{2}))?(AM|PM)$/);
    if (amPmMatch) {
      let h = parseInt(amPmMatch[1], 10);
      const m = parseInt(amPmMatch[2] || "00", 10);
      const period = amPmMatch[3];
      if (h < 1 || h > 12 || m < 0 || m > 59) return null;
      if (period === "AM") h = h === 12 ? 0 : h;
      if (period === "PM") h = h === 12 ? 12 : h + 12;
      return formatTime(h, m);
    }

    if (/^\d{1,2}$/.test(compact)) {
      const h = parseInt(compact, 10);
      if (h >= 0 && h <= 23) {
        return formatTime(h, 0);
      }
    }

    const digitOnly = compact.replace(/\D/g, "").slice(0, 4);
    if (digitOnly.length === 3 || digitOnly.length === 4) {
      const h = parseInt(digitOnly.slice(0, digitOnly.length - 2), 10);
      const m = parseInt(digitOnly.slice(-2), 10);
      if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
        return formatTime(h, m);
      }
    }

    if (/^\d{1,2}:\d{2}$/.test(compact)) {
      const [hRaw, mRaw] = compact.split(":");
      const h = parseInt(hRaw, 10);
      const m = parseInt(mRaw, 10);
      if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
        return formatTime(h, m);
      }
    }

    return null;
  };

  const handleInputChange = (e) => {
    const rawDigits = e.target.value.replace(/\D/g, "").slice(0, 4);
    let newValue = inputValue;

    if (rawDigits.length === 0) {
      newValue = "";
    } else if (rawDigits.length === 1) {
      newValue = rawDigits;
    } else if (rawDigits.length === 2) {
      const maybeHoursOrMinutes = parseInt(rawDigits, 10);
      if (maybeHoursOrMinutes <= 59) {
        // Permitimos temporalmente 00-59; en blur se normaliza.
        newValue = rawDigits;
      } else {
        return;
      }
    } else if (rawDigits.length === 3) {
      const firstTwo = parseInt(rawDigits.slice(0, 2), 10);
      const lastOne = parseInt(rawDigits.slice(2), 10);
      const firstOne = parseInt(rawDigits.slice(0, 1), 10);
      const lastTwo = parseInt(rawDigits.slice(1), 10);

      if (firstTwo <= 23 && lastOne <= 5) {
        newValue = `${rawDigits.slice(0, 2)}:${rawDigits.slice(2)}`;
      } else if (firstOne <= 23 && lastTwo <= 59) {
        // Soporta entradas como 930 -> 09:30
        newValue = `${String(firstOne).padStart(2, "0")}:${rawDigits.slice(1)}`;
      } else {
        return;
      }
    } else if (rawDigits.length === 4) {
      const hours = parseInt(rawDigits.slice(0, 2), 10);
      const minutes = parseInt(rawDigits.slice(2), 10);

      if (hours <= 23 && minutes <= 59) {
        newValue = `${rawDigits.slice(0, 2)}:${rawDigits.slice(2)}`;
      } else if (hours > 23 && hours <= 59 && minutes === 0) {
        // Compatibilidad con la regla solicitada: 40:00 -> 00:40
        newValue = `00:${rawDigits.slice(0, 2)}`;
      } else {
        return;
      }
    }

    const wasComplete = isValidTime24h(inputValue);
    const isNowComplete = isValidTime24h(newValue);

    setInputValue(newValue);

    // Solo propagamos cuando está completo y válido para evitar estados ambiguos.
    if (onChange) {
      if (newValue === "") {
        onChange(buildSyntheticEvent(""));
      } else if (isNowComplete) {
        onChange(buildSyntheticEvent(newValue));
      }
    }

    if (nextInputId && isNowComplete && !wasComplete) {
      focusNextInput();
    }
  };

  const handleArrowClick = (direction) => {
    const time = parseTime(inputValue);
    
    if (direction === 'up') {
      // Incrementar horas
      time.hours = (time.hours + 1) % 24;
    } else {
      // Decrementar horas
      time.hours = time.hours === 0 ? 23 : time.hours - 1;
    }
    
    const newValue = formatTime(time.hours, time.minutes);
    setInputValue(newValue);
    
    if (onChange) {
      onChange(buildSyntheticEvent(newValue));
    }
  };

  const handleBlur = (event) => {
    if (!inputValue) {
      notifyBlur(event);
      return;
    }

    const normalized = normalizeTo24h(inputValue);
    if (normalized === null) {
      const hourOverflowMatch = inputValue.match(/^(\d{2}):00$/);
      if (hourOverflowMatch) {
        const maybeMinutes = parseInt(hourOverflowMatch[1], 10);
        if (maybeMinutes > 23 && maybeMinutes <= 59) {
          const smartValue = formatTime(0, maybeMinutes);
          setInputValue(smartValue);
          if (onChange) {
            onChange(buildSyntheticEvent(smartValue));
          }
          notifyBlur(event, true);
          return;
        }
      }

      setInputValue("");
      if (onChange) {
        onChange(buildSyntheticEvent(""));
      }
      notifyBlur(event, true);
      return;
    }

    const valueChanged = normalized !== inputValue;
    setInputValue(normalized);
    if (onChange && (valueChanged || !isValidTime24h(inputValue))) {
      onChange(buildSyntheticEvent(normalized));
    }
    notifyBlur(event, valueChanged || !isValidTime24h(inputValue));
  };

  const handleFocus = (event) => {
    if (!selectOnFocus) return;
    const input = event.target;
    input.select();
    // El valor controlado recoloca el cursor al final al enfocar.
    setTimeout(() => input.select(), 0);
  };

  const handleClick = (event) => {
    if (!selectOnFocus) return;
    event.target.select();
  };

  // El clic suelta el cursor después del foco y deshace la selección.
  const handleMouseUp = (event) => {
    if (!selectOnFocus) return;
    event.preventDefault();
    event.target.select();
  };

  const handleKeyDown = (e) => {
    if (onKeyDown) {
      onKeyDown(e);
      if (e.defaultPrevented) return;
    }

    if (disabled) return;

    if (e.key === "Enter" && nextInputId) {
      const normalized = normalizeTo24h(inputValue);
      if (normalized && isValidTime24h(normalized)) {
        e.preventDefault();
        if (normalized !== inputValue) {
          commitValue(normalized);
        }
        focusNextInput();
        return;
      }
    }
    
    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        handleArrowClick('up');
        break;
      case 'ArrowDown':
        e.preventDefault();
        handleArrowClick('down');
        break;
    }
  };

  return (
    <div className="time-input-wrapper">
      <FormControl
        type="text"
        inputMode="numeric"
        pattern="^([01]\d|2[0-3]):([0-5]\d)$"
        id={id}
        aria-label={ariaLabel}
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onClick={handleClick}
        onMouseUp={handleMouseUp}
        onKeyDown={handleKeyDown}
        className={`form-control ${className}`}
        isInvalid={isInvalid}
        disabled={disabled}
        required={required}
        placeholder={placeholder || "HH:mm"}
        autoFocus={autoFocus}
      />
      {showDecorators && (
        <>
          <FiClock className="time-icon" />
          
          {/* Flechas sutiles dentro del input */}
          {showArrows && (
            <div className="time-arrows">
              <button
                type="button"
                className="arrow-btn up"
                onClick={() => handleArrowClick('up')}
                disabled={disabled}
                title="↑ para incrementar horas"
              >
                ▲
              </button>
              <button
                type="button"
                className="arrow-btn down"
                onClick={() => handleArrowClick('down')}
                disabled={disabled}
                title="↓ para decrementar horas"
              >
                ▼
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TimeInputWithArrows; 